import { getAccessToken } from "@/auth";
import type {
  AdminOverview,
  AppUser,
  Profile,
  ProfileOrigin,
  EastAfricanCommunity,
  City,
  NotificationLogEntry,
  Order,
  Promoter,
  PromoterApplication,
  Region,
  Ticket,
  TicketWithDetails,
  TourStopStatus,
  EventCategory,
  ProfileType,
  TourCampaignWithDetails,
  TourStopRevisionWithDetails,
  TourStopWithDetails,
  Venue,
  EventInvitation,
  OrganizerEventSummary,
  InvitationEventView,
  RsvpResponse,
  RegistryItemKind,
  RegistryItemPriority,
  RegistryReservationStatus,
  EventRegistryItem,
  RegistryItemView,
  PublicCatalogBootstrap,
  PublicRouteData,
  DashboardBootstrap,
} from "./types";

/**
 * HTTP client for the C# Azure Functions API in `api/`. This file is
 * the single chokepoint every page/component/action calls through —
 * nothing outside `lib/data/` should ever import the API directly, so
 * that backend changes (auth model, framework, storage) stay a
 * one-file change. See PLAN.md §11.
 *
 * Auth model: this API is never called from the browser, only from
 * this server-side module, which Auth.js has already authenticated
 * the request against. A shared secret proves "this came from our
 * Next.js server" — see api/Edaga.Events.Functions/Core/FunctionBase.cs
 * for the matching server-side check and the rationale.
 */
// 127.0.0.1, not localhost: the Functions host binds IPv4 only, so `localhost`
// resolves to ::1 first and every connection eats a ~200ms failover.
const API_URL = import.meta.env.VITE_API_URL ?? "/api/spa";

type ClientCacheEntry = {
  expiresAt: number;
  hasValue: boolean;
  value?: unknown;
  promise?: Promise<unknown>;
};

// Vite runs this module once for the lifetime of the SPA. Keep short-lived
// public GET results here so route changes reuse both completed and in-flight
// requests instead of asking the Functions app for identical datasets again.
const clientCache = new Map<string, ClientCacheEntry>();

const embeddedPublicBootstrap = readEmbeddedPublicBootstrap();
const embeddedPublicRouteData = readEmbeddedPublicRouteData();
let serverPublicRouteData: PublicRouteData | undefined;

function getCurrentPublicRouteData() {
  // Read again on demand as a safeguard for aggressively cached module entry
  // scripts that can begin evaluating before a later inert JSON script node.
  return embeddedPublicRouteData ?? readEmbeddedPublicRouteData() ?? serverPublicRouteData;
}

function readEmbeddedJson<T>(id: string): T | undefined {
  if (typeof document === "undefined") return undefined;
  const element = document.getElementById(id);
  if (!element?.textContent) return undefined;
  try {
    return JSON.parse(element.textContent) as T;
  } catch {
    return undefined;
  }
}

function readEmbeddedPublicBootstrap(): PublicCatalogBootstrap | undefined {
  return readEmbeddedJson<PublicCatalogBootstrap>("__EDAGA_PUBLIC_BOOTSTRAP__");
}

function readEmbeddedPublicRouteData(): PublicRouteData | undefined {
  return readEmbeddedJson<PublicRouteData>("__EDAGA_ROUTE_DATA__");
}

function withClientCache<T>(key: string, seconds: number, loader: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const existing = clientCache.get(key);
  if (existing && existing.expiresAt > now) {
    if (existing.promise) return existing.promise as Promise<T>;
    if (existing.hasValue) return Promise.resolve(existing.value as T);
  }

  const promise = loader()
    .then((value) => {
      clientCache.set(key, { expiresAt: Date.now() + seconds * 1_000, hasValue: true, value });
      return value;
    })
    .catch((error) => {
      if (clientCache.get(key)?.promise === promise) clientCache.delete(key);
      throw error;
    });
  clientCache.set(key, { expiresAt: now + seconds * 1_000, hasValue: false, promise });
  return promise;
}

async function apiFetch(
  path: string,
  init?: RequestInit,
  options?: { allowNotFound?: boolean; revalidateSeconds?: number },
): Promise<Response> {
  const accessToken = getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    cache: options?.revalidateSeconds ? "default" : "no-store",
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok && (res.status !== 404 || !options?.allowNotFound)) {
    const body = await res.text();
    let message = body;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      // not JSON — fall back to the raw body
    }
    throw new Error(message || `Request ${path} failed (${res.status})`);
  }
  return res;
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  const text = await res.text();
  if (!text) throw new Error(`Request returned an empty response (${res.status})`);
  return JSON.parse(text) as T;
}

async function apiGetCached<T>(path: string, revalidateSeconds = 30): Promise<T> {
  return withClientCache(`GET:${path}`, revalidateSeconds, async () => {
    const res = await apiFetch(path, undefined, { revalidateSeconds });
    const text = await res.text();
    if (!text) throw new Error(`Request returned an empty response (${res.status})`);
    return JSON.parse(text) as T;
  });
}

async function apiGetOptional<T>(path: string): Promise<T | undefined> {
  const res = await apiFetch(path, undefined, { allowNotFound: true });
  if (res.status === 404) return undefined;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : undefined;
}

async function apiGetOptionalCached<T>(path: string, revalidateSeconds = 30): Promise<T | undefined> {
  return withClientCache(`GET:${path}`, revalidateSeconds, async () => {
    const res = await apiFetch(path, undefined, { allowNotFound: true, revalidateSeconds });
    if (res.status === 404) return undefined;
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : undefined;
  });
}

async function apiSend<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ---------- Profiles ----------

export async function getPublicBootstrap(): Promise<PublicCatalogBootstrap> {
  return apiGetCached("/public/bootstrap");
}

export function getEmbeddedPublicBootstrap(): PublicCatalogBootstrap | undefined {
  return embeddedPublicBootstrap;
}

export function getEmbeddedTourStopBySlug(slug: string): TourStopWithDetails | undefined {
  const routeData = getCurrentPublicRouteData();
  if (routeData?.kind === "event" && routeData.value.slug === slug) {
    return routeData.value;
  }
  return embeddedPublicBootstrap?.tourStops.find((stop) => stop.slug === slug);
}

export function getEmbeddedRelatedPerformances(slug: string): TourStopWithDetails[] | undefined {
  const routeData = getCurrentPublicRouteData();
  return routeData?.kind === "event" && routeData.value.slug === slug ? routeData.related : undefined;
}

export function setServerPublicRouteData(routeData: PublicRouteData) {
  serverPublicRouteData = routeData;
}

export function initializeEmbeddedPublicRouteData() {
  serverPublicRouteData = readEmbeddedPublicRouteData();
}

export function getEmbeddedProfileBySlug(slug: string): Profile | undefined {
  const routeData = getCurrentPublicRouteData();
  if (routeData?.kind === "profile" && routeData.value.slug === slug) {
    return routeData.value;
  }
  return embeddedPublicBootstrap?.profiles.find((profile) => profile.slug === slug);
}

export function getEmbeddedCityBySlug(slug: string): City | undefined {
  const routeData = getCurrentPublicRouteData();
  if (routeData?.kind === "city" && routeData.value.slug === slug) {
    return routeData.value;
  }
  return embeddedPublicBootstrap?.cities.find((city) => city.slug === slug);
}

export function preloadPublicBootstrap(): Promise<PublicCatalogBootstrap> {
  return getPublicBootstrap();
}

export async function getProfiles(): Promise<Profile[]> {
  return (await getPublicBootstrap()).profiles;
}

export async function getAllProfilesAdmin(): Promise<Profile[]> {
  return apiGet("/profiles/admin");
}

export async function getProfilesForPromoter(promoterId: string): Promise<Profile[]> {
  return apiGet(`/promoters/${encodeURIComponent(promoterId)}/profiles`);
}

export async function getProfileBySlug(slug: string): Promise<Profile | undefined> {
  return apiGetOptionalCached(`/profiles/by-slug/${encodeURIComponent(slug)}`);
}

export async function getProfileById(id: string): Promise<Profile | undefined> {
  return apiGetOptional(`/profiles/${encodeURIComponent(id)}`);
}

export type CreateProfileInput = {
  name: string;
  nameAm?: string;
  nameTi?: string;
  origin: ProfileOrigin;
  communities?: EastAfricanCommunity[];
  genres: string[];
  bio: string;
  published?: boolean;
  profileType?: ProfileType;
  imageUrl?: string;
  heroImageUrl?: string;
  submissionSourceUrl?: string;
  promoterId?: string;
};

export async function createProfile(input: CreateProfileInput): Promise<Profile> {
  return apiSend("POST", "/profiles", input);
}

export async function updateProfile(id: string, input: CreateProfileInput): Promise<Profile> {
  return apiSend("PATCH", `/profiles/${encodeURIComponent(id)}`, input);
}

export async function setProfilePublished(id: string, published: boolean): Promise<Profile> {
  return apiSend("PATCH", `/profiles/${encodeURIComponent(id)}/publish`, { published });
}

// ---------- Cities ----------

export async function getCities(): Promise<City[]> {
  return (await getPublicBootstrap()).cities;
}

export const getCityBySlug = async (slug: string): Promise<City | undefined> => {
  return apiGetOptionalCached(`/cities/${encodeURIComponent(slug)}`);
};

export async function getNearestCities(cityId: string, limit = 4): Promise<City[]> {
  return apiGetCached(`/cities/${encodeURIComponent(cityId)}/nearest?limit=${limit}`);
}

// ---------- Tour stops ----------

export async function getAllTourStops(): Promise<TourStopWithDetails[]> {
  return (await getPublicBootstrap()).tourStops;
}

export async function getAllTourStopsAdmin(): Promise<TourStopWithDetails[]> {
  return apiGet("/tour-stops/admin");
}

export async function getTourStopBySlug(slug: string): Promise<TourStopWithDetails | undefined> {
  return apiGetOptionalCached(`/tour-stops/by-slug/${encodeURIComponent(slug)}`);
}

export function preloadTourStopBySlug(slug: string): Promise<TourStopWithDetails | undefined> {
  return getTourStopBySlug(slug);
}

export async function getTourStopById(id: string): Promise<TourStopWithDetails | undefined> {
  return apiGetOptional(`/tour-stops/${encodeURIComponent(id)}`);
}

export async function getUnlistedTourStop(id: string): Promise<TourStopWithDetails | undefined> {
  return apiGetOptional(`/tour-stops/unlisted/${encodeURIComponent(id)}`);
}

export async function getUpcomingForProfile(profileId: string): Promise<TourStopWithDetails[]> {
  return apiGetCached(`/profiles/${encodeURIComponent(profileId)}/tour-stops/upcoming`);
}

export async function getPastForProfile(profileId: string): Promise<TourStopWithDetails[]> {
  return apiGetCached(`/profiles/${encodeURIComponent(profileId)}/tour-stops/past`);
}

export async function getTourStopsForCity(citySlug: string): Promise<TourStopWithDetails[]> {
  return apiGetCached(`/cities/${encodeURIComponent(citySlug)}/tour-stops`);
}

export async function getFeaturedTourStops(limit = 6): Promise<TourStopWithDetails[]> {
  return apiGetCached(`/tour-stops/featured?limit=${limit}`);
}

// ---------- Featured tour campaigns ----------

export async function getActiveTourCampaigns(): Promise<TourCampaignWithDetails[]> {
  return (await getPublicBootstrap()).campaigns;
}

export async function getTourCampaignsAdmin(): Promise<TourCampaignWithDetails[]> {
  return apiGet("/tour-campaigns/admin");
}

export type UpsertTourCampaignInput = {
  title: string;
  eyebrow?: string;
  summary?: string;
  profileId: string;
  tourStopIds: string[];
  heroImageUrl?: string;
  published: boolean;
  displayOrder: number;
  featureStartsAtUtc?: string;
  featureEndsAtUtc?: string;
};

export async function createTourCampaign(input: UpsertTourCampaignInput): Promise<TourCampaignWithDetails> {
  return apiSend("POST", "/tour-campaigns", input);
}

export async function updateTourCampaign(
  id: string,
  input: UpsertTourCampaignInput,
): Promise<TourCampaignWithDetails> {
  return apiSend("PUT", `/tour-campaigns/${encodeURIComponent(id)}`, input);
}

export async function searchProfilesAndCities(query: string): Promise<{ profiles: Profile[]; cities: City[] }> {
  const q = query.trim();
  if (!q) return { profiles: [], cities: [] };
  return apiGet(`/search?q=${encodeURIComponent(q)}`);
}

// ---------- Users / follows ----------

export async function getUser(userId: string): Promise<AppUser | undefined> {
  return apiGetOptional(`/users/${encodeURIComponent(userId)}`);
}

export type UpdateUserPatch = {
  homeCityId?: string;
  whatsappNumber?: string;
  alertChannels?: { email: boolean; whatsapp: boolean };
};

export async function updateUser(userId: string, patch: UpdateUserPatch): Promise<AppUser> {
  return apiSend("PATCH", `/users/${encodeURIComponent(userId)}`, patch);
}

export async function ensureUser(id: string, defaults: { name: string; email: string }): Promise<AppUser> {
  return apiSend("POST", `/users/${encodeURIComponent(id)}/ensure`, defaults);
}

// ---------- Email/password auth ----------

export async function registerUser(input: { name: string; email: string; password: string }): Promise<AppUser> {
  return apiSend("POST", "/auth/register", input);
}

export async function loginUser(email: string, password: string): Promise<AppUser> {
  return apiSend("POST", "/auth/login", { email, password });
}

export async function verifyEmail(token: string): Promise<void> {
  await apiSend("POST", "/auth/verify-email", { token });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiSend("POST", "/auth/forgot-password", { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await apiSend("POST", "/auth/reset-password", { token, password });
}

export async function getFollowedProfiles(userId: string): Promise<Profile[]> {
  return apiGet(`/users/${encodeURIComponent(userId)}/follows`);
}

export async function isFollowingProfile(userId: string, profileId: string): Promise<boolean> {
  const { following } = await apiGet<{ following: boolean }>(
    `/users/${encodeURIComponent(userId)}/follows/${encodeURIComponent(profileId)}`
  );
  return following;
}

export async function followProfile(userId: string, profileId: string): Promise<void> {
  await apiSend("POST", `/users/${encodeURIComponent(userId)}/follows/${encodeURIComponent(profileId)}`);
}

export async function unfollowProfile(userId: string, profileId: string): Promise<void> {
  await apiSend("DELETE", `/users/${encodeURIComponent(userId)}/follows/${encodeURIComponent(profileId)}`);
}

export async function getUpcomingForFollowedProfiles(userId: string): Promise<TourStopWithDetails[]> {
  return apiGet(`/users/${encodeURIComponent(userId)}/upcoming`);
}

export async function getDashboardBootstrap(userId: string): Promise<DashboardBootstrap> {
  return apiGet(`/users/${encodeURIComponent(userId)}/dashboard`);
}

export async function getFollowerUserIds(profileIds: string[]): Promise<string[]> {
  return apiSend("POST", "/profiles/followers", { profileIds });
}

// ---------- Notifications ----------

export async function logNotification(
  entry: Omit<NotificationLogEntry, "id" | "createdAt">
): Promise<NotificationLogEntry> {
  return apiSend("POST", "/notifications", entry);
}

export async function getRecentNotifications(limit = 50): Promise<NotificationLogEntry[]> {
  return apiGet(`/notifications?limit=${limit}`);
}

// ---------- Orders / tickets ----------

export async function createOrder(input: {
  userId: string;
  tourStopId: string;
  items: { tierId: string; quantity: number }[];
}): Promise<{ order: Order; tickets: Ticket[] }> {
  return apiSend("POST", "/orders", input);
}

export async function createCheckoutSession(input: {
  userId: string;
  items: { tourStopId: string; tierId: string; quantity: number }[];
}): Promise<{ url: string }> {
  return apiSend("POST", "/payments/checkout-sessions", input);
}

export async function getOrdersForUser(userId: string): Promise<Order[]> {
  return apiGet(`/users/${encodeURIComponent(userId)}/orders`);
}

export async function getTicketsForUser(userId: string): Promise<TicketWithDetails[]> {
  return apiGet(`/users/${encodeURIComponent(userId)}/tickets`);
}

export async function getTicketById(ticketId: string): Promise<TicketWithDetails | undefined> {
  return apiGetOptional(`/tickets/${encodeURIComponent(ticketId)}`);
}

export async function getTicketBySerial(serial: string): Promise<TicketWithDetails | undefined> {
  return apiGetOptional(`/tickets/by-serial/${encodeURIComponent(serial)}`);
}

export async function scanTicket(
  ticketId: string,
  tourStopId: string,
  scannedByEmail: string
): Promise<{ ok: boolean; reason?: string; ticket?: TicketWithDetails; direction?: "in" | "out" }> {
  return apiSend("POST", `/tickets/${encodeURIComponent(ticketId)}/scan`, { tourStopId, scannedByEmail });
}

// ---------- Admin: sourcing / review queue ----------

export async function getAdminOverview(): Promise<AdminOverview> {
  return apiGet("/ops/overview");
}

export async function getVenues(): Promise<Venue[]> {
  return apiGet("/venues");
}

export async function getPromoters(): Promise<Promoter[]> {
  return apiGet("/promoters");
}

export async function getPromoterById(id: string): Promise<Promoter | undefined> {
  return apiGetOptional(`/promoters/${encodeURIComponent(id)}`);
}

export async function getTourStopsForPromoter(promoterId: string): Promise<TourStopWithDetails[]> {
  return apiGet(`/promoters/${encodeURIComponent(promoterId)}/tour-stops`);
}

export type TierSales = {
  tierId: string;
  name: string;
  priceCents: number;
  currency: string;
  quantity: number;
  sold: number;
};

export type TourStopSales = {
  tourStopId: string;
  ticketsSold: number;
  capacity: number;
  grossCents: number;
  currency?: string | null;
  checkedInCount: number;
  tiers: TierSales[];
};

export async function getPromoterSales(promoterId: string): Promise<TourStopSales[]> {
  return apiGet(`/promoters/${encodeURIComponent(promoterId)}/sales`);
}

export type PromoterAdminSummary = {
  promoter: Promoter;
  profileCount: number;
  eventCount: number;
  liveEventCount: number;
  ticketsSold: number;
  checkedInCount: number;
  revenue: { currency: string; grossCents: number }[];
};

export async function getPromoterAdminSummaries(): Promise<PromoterAdminSummary[]> {
  return apiGet("/promoters/admin/summaries");
}

export async function updatePromoter(
  id: string,
  input: { name: string; contactEmail?: string; verified: boolean },
): Promise<Promoter> {
  return apiSend("PATCH", `/promoters/${encodeURIComponent(id)}`, input);
}

export type CreatePromoterApplicationInput = {
  userId: string;
  organizationName: string;
  contactName: string;
  email: string;
  whatsappNumber?: string;
  city: string;
  pastEvents: string;
  profileHistory: string;
};

export async function createPromoterApplication(
  input: CreatePromoterApplicationInput
): Promise<PromoterApplication> {
  return apiSend("POST", "/promoter-applications", input);
}

export async function getPromoterApplicationsAdmin(): Promise<PromoterApplication[]> {
  return apiGet("/promoter-applications/admin");
}

export async function getPromoterApplicationsForUser(userId: string): Promise<PromoterApplication[]> {
  return (await apiGetOptional(`/users/${encodeURIComponent(userId)}/promoter-applications`)) ?? [];
}

export async function approvePromoterApplication(id: string): Promise<PromoterApplication> {
  return apiSend("POST", `/promoter-applications/${encodeURIComponent(id)}/approve`);
}

export async function rejectPromoterApplication(id: string, reason?: string): Promise<PromoterApplication> {
  return apiSend("POST", `/promoter-applications/${encodeURIComponent(id)}/reject`, { reason });
}

export type CreateTourStopInput = {
  title?: string;
  description?: string;
  dressCode?: string;
  ageRestriction?: string;
  attendeeRequirements?: string;
  accessibilityInfo?: string;
  capacity?: number;
  organizerContact?: string;
  showOrganizerContact?: boolean;
  hideExactAddress?: boolean;
  submissionIpHash?: string;
  visibility?: import("./types").EventVisibility;
  rsvpEnabled?: boolean;
  rsvpDeadline?: string;
  allowPlusOnes?: boolean;
  maxPlusOnes?: number;
  showGuestList?: boolean;
  profileIds: string[];
  cityId?: string;
  venueName: string;
  venueAddress: string;
  promoterName: string;
  category?: EventCategory;
  dateTime: string;
  endDateTime?: string;
  doorsTime?: string;
  timeTba?: boolean;
  status: TourStopStatus;
  externalTicketUrl?: string;
  ticketTiers?: { name: string; priceCents: number; currency: string; quantity: number; seatingEnabled?: boolean; rowCount?: number; seatsPerRow?: number }[];
  published?: boolean;
  newProfiles?: { name: string; origin: ProfileOrigin; referenceUrl?: string }[];
  cityName?: string;
  country?: string;
  countryCode?: string;
  region?: Region;
  cityLat?: number;
  cityLng?: number;
  googleMapsUrl?: string;
  googlePlaceId?: string;
  venueLat?: number;
  venueLng?: number;
  timeZoneId?: string;
  posterImageUrl?: string;
};

export async function createTourStop(input: CreateTourStopInput): Promise<TourStopWithDetails> {
  return apiSend("POST", "/tour-stops", input);
}

export async function updateTourStop(
  id: string,
  input: CreateTourStopInput,
): Promise<TourStopWithDetails> {
  return apiSend("PATCH", `/tour-stops/${encodeURIComponent(id)}`, input);
}

export async function createPromoterTourStop(
  promoterId: string,
  input: CreateTourStopInput
): Promise<TourStopWithDetails> {
  return apiSend("POST", `/promoters/${encodeURIComponent(promoterId)}/tour-stops`, input);
}

export async function createUserTourStop(
  userId: string,
  input: CreateTourStopInput
): Promise<TourStopWithDetails> {
  return apiSend("POST", `/users/${encodeURIComponent(userId)}/tour-stops`, input);
}

export async function updateUserTourStop(
  userId: string,
  id: string,
  input: CreateTourStopInput,
): Promise<TourStopWithDetails> {
  return apiSend("PATCH", `/users/${encodeURIComponent(userId)}/tour-stops/${encodeURIComponent(id)}`, input);
}

export async function getTourStopsForUser(userId: string): Promise<TourStopWithDetails[]> {
  return apiGet(`/users/${encodeURIComponent(userId)}/tour-stops`);
}

export async function setUserTourStopHidden(userId: string, id: string, hidden: boolean): Promise<TourStopWithDetails> {
  return apiSend("PATCH", `/users/${encodeURIComponent(userId)}/tour-stops/${encodeURIComponent(id)}/hidden`, { hidden });
}

export type EventReportReason = "duplicate" | "inappropriate" | "misleading" | "cancelled" | "other";

export async function reportTourStop(userId: string, id: string, reason: EventReportReason, details?: string) {
  return apiSend("POST", `/users/${encodeURIComponent(userId)}/tour-stops/${encodeURIComponent(id)}/reports`, { reason, details });
}

export async function updatePromoterTourStop(
  promoterId: string,
  id: string,
  input: CreateTourStopInput
): Promise<{ stop: TourStopWithDetails; revision?: TourStopRevisionWithDetails }> {
  return apiSend(
    "PATCH",
    `/promoters/${encodeURIComponent(promoterId)}/tour-stops/${encodeURIComponent(id)}`,
    input
  );
}

export async function getPendingTourStopRevisions(): Promise<TourStopRevisionWithDetails[]> {
  return apiGet("/tour-stop-revisions/pending");
}

export async function getTourStopRevisionsForPromoter(promoterId: string): Promise<TourStopRevisionWithDetails[]> {
  return apiGet(`/promoters/${encodeURIComponent(promoterId)}/tour-stop-revisions`);
}

export async function reviewTourStopRevision(
  id: string,
  approved: boolean,
  reason?: string,
): Promise<TourStopRevisionWithDetails> {
  return apiSend("POST", `/tour-stop-revisions/${encodeURIComponent(id)}/review`, { approved, reason });
}

export async function setPromoterTourStopStatus(
  promoterId: string,
  id: string,
  status: TourStopStatus,
  reason?: string,
): Promise<TourStopWithDetails> {
  return apiSend(
    "PATCH",
    `/promoters/${encodeURIComponent(promoterId)}/tour-stops/${encodeURIComponent(id)}/status`,
    { status, reason }
  );
}

export async function setTourStopPublished(
  id: string,
  published: boolean
): Promise<{ stop: TourStopWithDetails; wasPublished: boolean }> {
  return apiSend("PATCH", `/tour-stops/${encodeURIComponent(id)}/publish`, { published });
}
// ---------- Private events / invitations ----------

export async function getOrganizerEvent(userId: string, eventId: string): Promise<OrganizerEventSummary> {
  return apiGet(`/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/organizer`);
}

export async function createEventInvitations(
  userId: string,
  eventId: string,
  invitees: { name?: string; email?: string; phone?: string; allowedPartySize?: number }[],
): Promise<{ invitation: EventInvitation; inviteUrl: string }[]> {
  return apiSend("POST", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/invitations`, { invitees });
}

export async function revokeEventInvitation(userId: string, eventId: string, invitationId: string): Promise<void> {
  await apiSend("DELETE", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/invitations/${encodeURIComponent(invitationId)}`);
}

export async function resendEventInvitations(
  userId: string,
  eventId: string,
  invitationIds: string[],
): Promise<{ resent: number; invitationIds: string[] }> {
  return apiSend("POST", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/invitations/resend`, { invitationIds });
}

export async function getInvitationEvent(token: string): Promise<InvitationEventView | undefined> {
  return apiGetOptional(`/invitations/${encodeURIComponent(token)}`);
}

export async function respondToInvitation(
  token: string,
  input: { response: RsvpResponse; partySize: number; guestNames?: string; dietaryNotes?: string; accessibilityNotes?: string; message?: string },
): Promise<EventInvitation> {
  return apiSend("POST", `/invitations/${encodeURIComponent(token)}/rsvp`, input);
}

export type RegistryItemInput = {
  kind: RegistryItemKind;
  title: string;
  description?: string;
  imageUrl?: string;
  retailerName?: string;
  retailerUrl?: string;
  estimatedPriceCents?: number;
  goalAmountCents?: number;
  currency: string;
  quantityRequested: number;
  priority: RegistryItemPriority;
  allowGroupContributions: boolean;
  sortOrder: number;
};

export async function updatePrivateEventTheme(
  userId: string,
  eventId: string,
  input: { preset: string; primaryColor: string; accentColor: string; font: string; coverImageUrl?: string },
): Promise<TourStopWithDetails> {
  return apiSend("PATCH", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/theme`, input);
}

export async function createEventRegistryItem(
  userId: string,
  eventId: string,
  input: RegistryItemInput,
): Promise<EventRegistryItem> {
  return apiSend("POST", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/registry`, input);
}

export async function updateEventRegistryItem(
  userId: string,
  eventId: string,
  itemId: string,
  input: RegistryItemInput,
): Promise<EventRegistryItem> {
  return apiSend("PATCH", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/registry/${encodeURIComponent(itemId)}`, input);
}

export async function archiveEventRegistryItem(userId: string, eventId: string, itemId: string): Promise<void> {
  await apiSend("DELETE", `/users/${encodeURIComponent(userId)}/events/${encodeURIComponent(eventId)}/registry/${encodeURIComponent(itemId)}`);
}

export async function saveEventRegistryReservation(
  token: string,
  itemId: string,
  input: { quantity: number; contributionCents: number; status: RegistryReservationStatus; note?: string },
): Promise<RegistryItemView> {
  return apiSend("POST", `/invitations/${encodeURIComponent(token)}/registry/${encodeURIComponent(itemId)}`, input);
}
